import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Search, Plus, ShieldAlert, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";

export default function Patients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("patients");
  const [addOpen, setAddOpen] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", dob: "", id_number: "", scheme: "", member_no: "" });
  const [lead, setLead] = useState({ full_name: "", phone: "", email: "", reason: "" });

  const canAdd = ["admin", "practitioner", "receptionist"].includes(user?.role);
  const canLead = ["admin", "receptionist"].includes(user?.role);

  const { data: patients = [], isLoading } = useQuery({
    queryKey: ["patients"],
    queryFn: async () => (await api.get("/patients")).data,
  });

  const addMut = useMutation({
    mutationFn: async (body) => (await api.post("/patients", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["patients"] }); setAddOpen(false); toast.success("Patient added"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const leadMut = useMutation({
    mutationFn: async (body) => (await api.post("/patients/lead", body)).data,
    onSuccess: () => { setLeadOpen(false); toast.success("Lead created — intake form dispatched via n8n (WhatsApp + email)"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const filtered = patients.filter((p) =>
    (p.full_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (p.file_number || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Patients</h1>
          <p className="mt-1 text-sm text-muted-foreground">{patients.length} on file</p>
        </div>
        <div className="flex gap-2">
          {canLead && (
            <Dialog open={leadOpen} onOpenChange={setLeadOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="new-lead-btn" className="gap-2 rounded-full">
                  <MessageSquarePlus className="h-4 w-4" /> Quick lead
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-popover">
                <DialogHeader>
                  <DialogTitle>New patient lead</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-muted-foreground">
                  Type a name + phone. Practice OS fires <code>patient.intake_requested</code> to n8n which
                  WhatsApps the intake form. On submit, the patient is auto-created.
                </p>
                <div className="space-y-3">
                  <div><Label>Full name</Label><Input data-testid="lead-name" value={lead.full_name} onChange={(e) => setLead({ ...lead, full_name: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input data-testid="lead-phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="+27 83 ..." /></div>
                  <div><Label>Email (optional)</Label><Input data-testid="lead-email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} /></div>
                  <div><Label>Reason</Label><Input data-testid="lead-reason" value={lead.reason} onChange={(e) => setLead({ ...lead, reason: e.target.value })} placeholder="e.g. cleaning" /></div>
                </div>
                <DialogFooter>
                  <Button data-testid="lead-submit" onClick={() => leadMut.mutate(lead)} disabled={!lead.full_name || !lead.phone || leadMut.isPending} className="rounded-full">
                    Send intake form
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {canAdd && (
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-patient-btn" className="gap-2 rounded-full">
                  <Plus className="h-4 w-4" /> New patient
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-popover">
                <DialogHeader><DialogTitle>Add patient</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Full name</Label><Input data-testid="patient-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input data-testid="patient-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div><Label>Email</Label><Input data-testid="patient-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div><Label>Date of birth</Label><Input type="date" data-testid="patient-dob" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></div>
                  <div><Label>SA ID number</Label><Input data-testid="patient-id" value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} /></div>
                  <div><Label>Medical aid</Label><Input data-testid="patient-scheme" value={form.scheme} onChange={(e) => setForm({ ...form, scheme: e.target.value })} placeholder="Discovery" /></div>
                  <div><Label>Member no.</Label><Input data-testid="patient-member" value={form.member_no} onChange={(e) => setForm({ ...form, member_no: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button
                    data-testid="patient-submit"
                    className="rounded-full"
                    disabled={!form.full_name || addMut.isPending}
                    onClick={() => addMut.mutate({
                      full_name: form.full_name, phone: form.phone, email: form.email,
                      dob: form.dob, id_number: form.id_number,
                      medical_aid: form.scheme ? { scheme: form.scheme, member_no: form.member_no } : null,
                    })}
                  >
                    Save patient
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {(user?.role === "receptionist" || user?.role === "billing") && (
        <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-secondary/40 px-4 py-2.5 text-xs text-muted-foreground">
          <ShieldAlert className="h-4 w-4 text-primary" /> POPIA masking active — clinical details and full ID numbers are hidden for your role.
        </div>
      )}

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="patient-search" className="pl-9" placeholder="Search name or file number…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <Card className="overflow-hidden rounded-2xl border-border/60">
        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-12 text-center text-muted-foreground">
            <Users className="h-8 w-8" /> No patients found.
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {filtered.map((p) => (
              <button
                key={p.id}
                data-testid={`patient-row-${p.id}`}
                onClick={() => navigate(`/app/patients/${p.id}`)}
                className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-secondary/50"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                  {p.full_name?.charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{p.full_name}</div>
                  <div className="text-xs text-muted-foreground">{p.file_number} · {p.phone || "—"}</div>
                </div>
                {p.medical_aid?.scheme && (
                  <Badge variant="secondary" className="rounded-full">{p.medical_aid.scheme}</Badge>
                )}
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
